<script>
	// 통계 대시보드 (공유) — 봇 방문/클릭 분석.
	// 봇 빌더 "통계" 탭과 Main 콘솔 "통계" 섹션이 같은 컴포넌트를 botId만 바꿔 렌더한다.
	// ApexCharts 제거 → shadcn(--sb-*) 토큰 기반 SVG 차트(MiniChart)로 재구축.
	// dataviz 원칙(듀얼축 금지): 방문/클릭을 "단일 시리즈 2개 차트(small multiples)"로 나눔.
	import { PRConnect } from "@projectroom/ui-lib";
	import moment from "moment-timezone";
	import MiniChart from "./MiniChart.svelte";

	export let botId; // 대상 봇 id (없으면 선택 안내)
	export let locked = false; // 플랜 미지원 시 잠금 오버레이
	export let embedded = false; // Main 콘솔에 임베드 시 내부 제목(h3) 숨김(섹션 헤더와 중복 방지)

	let loading = false;
	let loaded = false;
	let mode = "weekly"; // weekly | monthly

	// 서버 원본 4배열: {date|yearMonth, cnt}
	let visitsW = [];
	let visitsM = [];
	let clicksW = [];
	let clicksM = [];
	let botData = null; // 게시상태·공유URL 파생용(빈 화면 안내 카드에서 사용)

	const load = (id) => {
		if (!id) return;
		loading = true;
		loaded = false;
		PRConnect.send("/api/Bot/getBotInfoByBotId", { id }, (data) => {
			loading = false;
			if (data.header.status !== "success") return;
			const r = data.result || {};
			visitsW = r.botAccessLogWeekly || [];
			visitsM = r.botAccessLogMonthly || [];
			clicksW = r.buttonAccessLogWeekly || [];
			clicksM = r.buttonAccessLogMonthly || [];
			botData = r.botData || null;
			loaded = true;
		});
	};

	// botId가 바뀔 때마다 재조회(Main 프로젝트 전환 대응)
	let lastId;
	$: if (botId && botId !== lastId) {
		lastId = botId;
		load(botId);
	}

	// 현재 모드의 {label,value} 시리즈로 정규화
	const toSeries = (arr, m) =>
		(arr || []).map((it) => ({
			label: m === "weekly" ? moment(it.date).format("MM-DD") : moment(it.yearMonth).format("YY-MM"),
			value: parseInt(it.cnt, 10) || 0,
		}));

	$: visits = toSeries(mode === "weekly" ? visitsW : visitsM, mode);
	$: clicks = toSeries(mode === "weekly" ? clicksW : clicksM, mode);

	// 헤드라인 stat
	const sum = (s) => s.reduce((n, d) => n + d.value, 0);
	$: totalVisits = sum(visits);
	$: totalClicks = sum(clicks);
	$: avgVisits = visits.length ? Math.round(totalVisits / visits.length) : 0;
	$: ctr = totalVisits ? Math.round((totalClicks / totalVisits) * 100) : 0;

	// 빈 화면(데이터 0) 안내: 게시 여부 + 공유 URL을 응답에서 자체 파생(빌더·Main 공통)
	$: isEmpty = loaded && totalVisits === 0 && totalClicks === 0;
	$: published = !!(botData && botData.lastPostDate);
	$: shareUrl = botData
		? botData.pageUrl || (botData.designateUrl ? `${location.origin}/view/@${botData.designateUrl}` : "")
		: "";
	let copied = false;
	const copyLink = async () => {
		if (!shareUrl) return;
		try {
			await navigator.clipboard.writeText(shareUrl);
			copied = true;
			setTimeout(() => (copied = false), 1500);
		} catch (e) {
			/* clipboard 미지원 무시 */
		}
	};
	const openPreview = () => shareUrl && window.open(shareUrl, "_blank");

	const fmt = (n) => (n || 0).toLocaleString();
</script>

<div class="stat-dash tw-font-sans tw-text-foreground">
	<div class="head">
		<div class="head-l">
			{#if !embedded}<h3>통계</h3>{/if}
			<span class="pill">방문 · 클릭</span>
		</div>
		{#if botId && !locked}
			<div class="seg" role="tablist" aria-label="기간">
				<button type="button" class:on={mode === "weekly"} on:click={() => (mode = "weekly")}>주간</button>
				<button type="button" class:on={mode === "monthly"} on:click={() => (mode = "monthly")}>월간</button>
			</div>
		{/if}
	</div>

	{#if !botId}
		<div class="empty">봇을 선택하면 방문·클릭 통계를 볼 수 있어요.</div>
	{:else if locked}
		<div class="empty lock">
			<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
			현재 플랜에서는 통계가 지원되지 않아요.
		</div>
	{:else if loading && !loaded}
		<div class="empty">불러오는 중…</div>
	{:else}
		<!-- 뼈대(0 타일)는 빈 화면에서도 유지 → 대시보드가 살아있게 보이도록 -->
		<div class="stats">
			<div class="stat"><div class="stat-n">{fmt(totalVisits)}</div><div class="stat-l">총 방문수</div></div>
			<div class="stat"><div class="stat-n">{fmt(totalClicks)}</div><div class="stat-l">총 클릭수</div></div>
			<div class="stat"><div class="stat-n">{fmt(avgVisits)}</div><div class="stat-l">{mode === "weekly" ? "일 평균 방문" : "월 평균 방문"}</div></div>
			<div class="stat"><div class="stat-n">{ctr}%</div><div class="stat-l">방문 대비 클릭률</div></div>
		</div>

		{#if isEmpty}
			<!-- 차트 자리에만 안내 카드: 왜 비었는지 + 다음 할 일(게시 상태별 분기) -->
			<div class="guide">
				{#if !published}
					<svg class="guide-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
						<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
						<path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
						<path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
					</svg>
					<div class="guide-t">아직 게시되지 않았어요</div>
					<div class="guide-d">페이지를 <b>게시</b>하면 방문·클릭 통계가 여기에 집계돼요.</div>
				{:else}
					<svg class="guide-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
						<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
						<line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>
					</svg>
					<div class="guide-t">아직 방문이 없어요</div>
					<div class="guide-d">이 링크를 공유하면 방문·클릭이 집계돼요.</div>
					<div class="guide-actions">
						<button type="button" class="ga-btn primary" on:click={copyLink}>{copied ? "복사됨!" : "링크 복사"}</button>
						{#if shareUrl}<button type="button" class="ga-btn" on:click={openPreview}>미리보기</button>{/if}
					</div>
					{#if shareUrl}<div class="guide-url" title={shareUrl}>{shareUrl}</div>{/if}
				{/if}
			</div>
		{:else}
			<div class="charts">
				<MiniChart title="방문 추이" subtitle={mode === "weekly" ? "최근 주간" : "최근 월간"} kind="line" series={visits} />
				<MiniChart title="클릭 추이" subtitle={mode === "weekly" ? "최근 주간" : "최근 월간"} kind="bar" series={clicks} />
			</div>
		{/if}
	{/if}
</div>

<style>
	.stat-dash {
		padding: 10px;
	}
	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		margin-bottom: 14px;
	}
	.head-l {
		display: flex;
		align-items: center;
		gap: 8px;
	}
	.head h3 {
		margin: 0;
		font-size: 15px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
	.pill {
		border-radius: 999px;
		background: hsl(var(--sb-muted));
		padding: 2px 8px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.seg {
		display: inline-flex;
		padding: 2px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-muted) / 0.4);
	}
	.seg button {
		border: none;
		background: transparent;
		padding: 4px 12px;
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
		border-radius: 6px;
		cursor: pointer;
	}
	.seg button.on {
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-primary));
		box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
	}
	.empty {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 40px 16px;
		text-align: center;
		font-size: 13px;
		color: hsl(var(--sb-muted-foreground));
		border: 1px dashed hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-muted) / 0.3);
	}
	.empty.lock svg {
		width: 18px;
		height: 18px;
	}
	.stats {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 8px;
		margin-bottom: 14px;
	}
	.stat {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		padding: 10px 12px;
		background: hsl(var(--sb-card));
	}
	.stat-n {
		font-size: 20px;
		font-weight: 700;
		color: hsl(var(--sb-foreground));
		line-height: 1.1;
		font-variant-numeric: tabular-nums;
	}
	.stat-l {
		margin-top: 3px;
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
	}
	.charts {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 12px;
	}

	/* 빈 화면 안내 카드(차트 자리) */
	.guide {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 6px;
		padding: 36px 20px;
		text-align: center;
		border: 1px dashed hsl(var(--sb-border));
		border-radius: 10px;
		background: hsl(var(--sb-muted) / 0.3);
	}
	.guide-ic {
		width: 34px;
		height: 34px;
		color: hsl(var(--sb-primary));
		opacity: 0.85;
		margin-bottom: 2px;
	}
	.guide-t {
		font-size: 15px;
		font-weight: 700;
		color: hsl(var(--sb-foreground));
	}
	.guide-d {
		max-width: 340px;
		font-size: 13px;
		line-height: 1.6;
		color: hsl(var(--sb-muted-foreground));
	}
	.guide-actions {
		display: flex;
		gap: 8px;
		margin-top: 10px;
	}
	.ga-btn {
		padding: 8px 16px;
		font-size: 13px;
		font-weight: 600;
		border-radius: 8px;
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-foreground));
		cursor: pointer;
	}
	.ga-btn.primary {
		background: hsl(var(--sb-primary));
		border-color: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
	}
	.guide-url {
		margin-top: 10px;
		max-width: 100%;
		font-size: 11.5px;
		color: hsl(var(--sb-muted-foreground));
		word-break: break-all;
		overflow-wrap: anywhere;
	}
	@media (max-width: 720px) {
		.charts {
			grid-template-columns: 1fr;
		}
		.stats {
			grid-template-columns: repeat(2, 1fr);
		}
	}
</style>
